import React, {ReactNode, useEffect} from 'react';
import {createPortal} from 'react-dom';

/*
 * The Account page's furniture (Android's AccountKit.kt): group titles over
 * the cards, the bordered card, the green and outline pills, the chevron
 * text button, hints and the tinted and tan boxes, the floating-label field,
 * and the Cancel / Save pair at the foot of a card being edited (the web's
 * own: Android's edit forms are full pages with Save at their foot).
 */

export type Direction = 'right' | 'down' | 'left' | 'up';
const ROTATE: Record<Direction, number> = {right: 0, down: 90, left: 180, up: 270};

/** MenuIcons.Chevron, turned to face [direction]. */
export const Chevron = ({direction, size = 15, color}: {direction: Direction; size?: number; color?: string}) => (
  <svg className="ac-chevron" viewBox="0 0 24 24" width={size} height={size} aria-hidden="true"
       style={{transform: `rotate(${ROTATE[direction]}deg)`, color}}>
    <path d="M9 5.5L15.5 12L9 18.5" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export const GroupTitle = ({text, first}: {text: string; first?: boolean}) => (
  <div className={first ? 'ac-group ac-group--first' : 'ac-group'}>{text}</div>
);

export const Card = React.forwardRef<HTMLDivElement, {children: ReactNode; plain?: boolean; className?: string}>(
  ({children, plain, className}, ref) => (
    <div ref={ref} className={['ac-card', plain ? 'ac-card--plain' : '', className ?? ''].join(' ').trim()}>{children}</div>
  ));

export const Hairline = ({green, thick}: {green?: boolean; thick?: boolean}) =>
  <div className={['ac-hairline', green ? 'ac-hairline--green' : '', thick ? 'ac-hairline--thick' : ''].join(' ').trim()}/>;

export const InitialsBadge = ({text}: {text: string}) => <div className="ac-badge">{text}</div>;

export const OutlinePill = ({text, onClick, disabled, label, tall}: {
  text: string; onClick: () => void; disabled?: boolean; label?: string; tall?: boolean;
}) => (
  <button className={tall ? 'ac-outline ac-outline--tall' : 'ac-outline'} onClick={onClick} disabled={disabled} aria-label={label}>{text}</button>
);

export const PrimaryPill = ({text, onClick, disabled}: {text: string; onClick: () => void; disabled?: boolean}) => (
  <button className="ac-primary" onClick={onClick} disabled={disabled}>{text}</button>
);

export const ChevronButton = ({text, direction, onClick}: {text: string; direction: Direction; onClick: () => void}) => (
  <>
    <Hairline/>
    <button className="ac-chevron-button" onClick={onClick}>{text}<Chevron direction={direction}/></button>
  </>
);

export const Hint = ({children, className}: {children: ReactNode; className?: string}) =>
  <div className={className ? `ac-hint ${className}` : 'ac-hint'}>{children}</div>;

export const SmallCaps = ({text}: {text: string}) => <div className="ac-smallcaps">{text}</div>;

export const NoticeBox = ({text, action}: {text: string; action?: ReactNode}) => (
  <div className="ac-notice">{text}{action && <div className="ac-notice-action">{action}</div>}</div>
);

/** FloatingLabelField: an outlined box with its label sitting on the top border. */
export const Field = ({label, value, onChange, placeholder, disabled, inputMode, className, type, autoComplete}: {
  label: string; value: string; onChange?: (v: string) => void; placeholder?: string; disabled?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']; className?: string; type?: string; autoComplete?: string;
}) => (
  <label className={['ac-field', disabled ? 'ac-field--off' : '', className ?? ''].join(' ').trim()}>
    <span className="ac-field-label">{label}</span>
    <input className="ac-input" value={value} placeholder={placeholder} disabled={disabled} inputMode={inputMode} type={type}
           autoComplete={autoComplete}
           onChange={e => onChange?.(e.target.value)}/>
  </label>
);

/** The foot of a card in edit mode: Cancel and the save, side by side, phone width (AddEstimatesSheet's pair). */
export const EditFoot = ({saveLabel, onSave, onCancel, ready = true, note}: {
  saveLabel: string; onSave: () => void; onCancel: () => void; ready?: boolean; note?: string;
}) => (
  <div className="ac-edit-foot">
    <div className="ac-edit-buttons">
      <button className="ac-cancel" onClick={onCancel}>Cancel</button>
      <button className="ac-primary ac-primary--pair" onClick={onSave} disabled={!ready}>{saveLabel}</button>
    </div>
    {note && <Hint className="ac-edit-note">{note}</Hint>}
  </div>
);

/** A small dialog over the page (Android's Dialog cards); Escape or a press on the scrim closes it. */
export const Dialog = ({title, label, onClose, children}: {title: string; label?: string; onClose: () => void; children: ReactNode}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  // Over everything, not inside the page's scrolling column.
  return createPortal(
    <div className="ac ac-scrim" onClick={onClose}>
      <div className="ac-dialog" role="dialog" aria-modal="true" aria-label={label ?? title} onClick={e => e.stopPropagation()}>
        <div className="ac-dialog-title">{title}</div>
        {children}
      </div>
    </div>,
    document.body,
  );
};
